import clsx from 'clsx'
import { useRef } from 'react'
import { Link } from 'react-router-dom'

export type MatterRecordsItemProps = {
  /** Row label */
  label: string
  /** Icon component */
  icon?: React.FC<React.SVGProps<SVGSVGElement>>
  /** The route to navigate to. Omit to render as a button (e.g. "Build a new record"). */
  to?: string
  /** Whether this is the currently active record (link rows only) */
  isActive?: boolean
  /** Click handler */
  onClick?: () => void
  /** Optional trailing slot, e.g. a beta tag (link rows only) */
  trailingAction?: React.ReactNode
}

const kRowShell = 'group flex w-full items-center gap-2 rounded-md pl-2 pr-1 py-1 h-9 min-h-9 overflow-hidden text-left align-center justify-center'

/**
 * A single row in the Matter Records sidebar section.
 *
 * Link rows (`to` provided) use lighter `brand-neutral-800` text to match the chat history
 * item style. Button rows (no `to`, e.g. "Build a new record") use darker `brand-neutral-950`
 * per Figma — consistent with other action buttons in the nav.
 */
export function MatterRecordsItem({ label, icon: Icon, to, isActive = false, onClick, trailingAction }: MatterRecordsItemProps) {
  const rowRef = useRef<HTMLDivElement>(null)

  const handleLinkKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowRight') return
    const trigger = rowRef.current?.querySelector('button')
    if (trigger) {
      event.preventDefault()
      trigger.focus()
    }
  }

  if (!to) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          kRowShell,
          'text-text-primary',
          'focus:outline-hidden focus-visible:shadow-[0px_0px_0px_2px_rgba(42,73,255)_inset]',
          'hover:bg-surface-hover active:bg-surface-hover'
        )}
      >
        {Icon && <Icon className="h-4 w-4 shrink-0 text-text-primary" aria-hidden="true" />}
        <span className="flex-1 overflow-hidden whitespace-nowrap text-xs">{label}</span>
      </button>
    )
  }

  return (
    <div
      ref={rowRef}
      className={clsx(
        kRowShell,
        'has-[:focus-visible]:shadow-[0px_0px_0px_2px_rgba(42,73,255)_inset]',
        isActive ? 'bg-surface-selected-subtle font-medium text-text-primary' : 'text-text-secondary hover:bg-surface-hover active:bg-surface-hover'
      )}
    >
      <Link
        to={to}
        onClick={onClick}
        onKeyDown={handleLinkKeyDown}
        title={label}
        className="flex flex-1 self-stretch items-center gap-2 min-w-0 overflow-hidden rounded-md focus:outline-hidden"
        aria-current={isActive ? 'page' : undefined}
      >
        {Icon && <Icon className={clsx('h-4 w-4 shrink-0', isActive ? 'text-text-primary' : 'text-text-secondary')} aria-hidden="true" />}
        <span className="flex-1 overflow-hidden whitespace-nowrap text-xs">{label}</span>
      </Link>
      {trailingAction && <div className="flex items-center justify-center">{trailingAction}</div>}
    </div>
  )
}
